import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "statusBar",
    "toggleButton",
    "mapContainer1",
    "mapContainer2",
  ];
  static values = {
    maxBounds: { type: Array, default: null },
    centrePoint: { type: Array, default: null },
    siteSize: { type: Number, default: null },
    superAdmin: { type: Boolean, default: null },
    userToken: { type: String, default: null },
    siteId: { type: Number, default: null },
    mapLoaded: { type: Boolean, default: false },
    public: { type: Boolean, default: false },
    hideUnrated: { type: Boolean, default: false },
    disableZoom: { type: Boolean, default: null },
    pinImageUrl: { type: String, default: null },
    pinClusterImageUrl: { type: String, default: null },
  };

  connect() {
    this.lastSelectedRouteColors = [];
    this.tile_params = "";
    this.initColours();
    this.activeMap = "comfort"; // Start with comfort_avg visible
    this.syncingPan = false; // Flag to prevent infinite loop during synchronization
    this.initBothMaps();

    const fullScreen = document.getElementById("js-full-screen");
    fullScreen &&
      fullScreen.addEventListener("click", () => {
        setTimeout(() => {
          this.map1.resize();
          this.map2.resize();
        }, 50);
      });

    const mapZoomIn = document.getElementById("js-map-zoom-in");
    mapZoomIn &&
      mapZoomIn.addEventListener("click", () => {
        this.map1.zoomIn();
        this.map2.zoomIn();
      });

    const mapZoomOut = document.getElementById("js-map-zoom-out");
    mapZoomOut &&
      mapZoomOut.addEventListener("click", () => {
        this.map1.zoomOut();
        this.map2.zoomOut();
      });

    const getCurrentLocation = document.getElementById("js-get-location");
    getCurrentLocation &&
      getCurrentLocation.addEventListener("click", () => {
        this.getCurrentLocation();
      });
  }

  initBothMaps() {
    this.line_width = 4;
    this.line_scale = "none";
    this.defaultZoom = 5;

    this.setBaseUrl();
    let params = new URLSearchParams(document.location.search);
    window.mapLoadedValue = false;

    mapboxgl.accessToken =
      "pk.eyJ1IjoiY29ycmVjdGVkdGltZSIsImEiOiJZQWpHMy1JIn0.wtBkQp-NkyRbRdcot5o-9g";

    // Initialize first map (comfort_avg)
    this.map1 = new mapboxgl.Map({
      container: "map1",
      style: "mapbox://styles/mapbox/light-v11",
      center: this.center(),
      bounds: this.bounds(),
      maxBounds: this.maxBounds(),
    });

    // Initialize second map (public_avg)
    this.map2 = new mapboxgl.Map({
      container: "map2",
      style: "mapbox://styles/mapbox/light-v11",
      center: this.center(),
      bounds: this.bounds(),
      maxBounds: this.maxBounds(),
    });

    // Set window.map for backward compatibility with other code
    window.map = this.map1;

    // Set initial visibility - hide map2 by default
    this.mapContainer2Target.style.visibility = "hidden";

    // Setup both maps on load
    this.map1.on("load", () => {
      window.mapLoadedValue = true;
      this.hideLoader();
      this.initComfortMap(params, this.map1, "comfort_avg");
      this.drawBounds(this.map1);
      this.setInitialLineThickness(3, this.map1, "comfort_avg");
      let initialTileParams = this.generateTileParams();
      this.updateTileParams(initialTileParams, true, this.map1, "comfort_avg");

      this.setDefaultZoom(params, this.map1);

      // Disable zoom and drag on rmr
      if (this.disableZoomValue) {
        this.map1.scrollZoom.disable();
        this.map1.dragPan.disable();
        this.map1.setZoom(12);
        this.setLineThickness(2, this.map1, "comfort_avg");
      }

      this.setupURLTracking(this.map1);
      this.setupZoomWarning(this.map1);
      this.applyColours(this.map1, "comfort_avg");
    });

    this.map2.on("load", () => {
      // Add watermark to distinguish public_avg map without altering base colours
      this.addPublicMapWatermark();

      this.initComfortMap(params, this.map2, "public_avg");
      this.drawBounds(this.map2);
      this.setInitialLineThickness(3, this.map2, "public_avg");
      let initialTileParams = this.generateTileParams();
      this.updateTileParams(initialTileParams, true, this.map2, "public_avg");

      this.setDefaultZoom(params, this.map2);

      if (this.disableZoomValue) {
        this.map2.scrollZoom.disable();
        this.map2.dragPan.disable();
        this.map2.setZoom(12);
        this.setLineThickness(2, this.map2, "public_avg");
      }

      this.setupURLTracking(this.map2);
      this.setupZoomWarning(this.map2);
      this.applyColours(this.map2, "public_avg");
    });

    // Sync pan and zoom between maps
    this.setupMapSync();

    this.map1.on("idle", () => {
      if (!window.mapLoadedValue) {
        this.showLoader();
      } else {
        this.hideLoader();
      }

      // Set bounds used by export by the current map view
      let bounds = this.map1.getBounds();
      let mapElement = document.getElementById("map-container");
      mapElement.dataset.bounds = [
        bounds.getNorth(),
        bounds.getWest(),
        bounds.getEast(),
        bounds.getSouth(),
      ];
    });

    this.map1.on("sourcedata", (event) => {
      if (!event.isSourceLoaded) {
        this.showLoader();
      }
    });
  }

  setupMapSync() {
    // Sync map1 to map2
    this.map1.on("move", () => {
      if (!this.syncingPan) {
        this.syncingPan = true;
        this.map2.setCenter(this.map1.getCenter());
        this.map2.setZoom(this.map1.getZoom());
        this.syncingPan = false;
      }
    });

    // Sync map2 to map1
    this.map2.on("move", () => {
      if (!this.syncingPan) {
        this.syncingPan = true;
        this.map1.setCenter(this.map2.getCenter());
        this.map1.setZoom(this.map2.getZoom());
        this.syncingPan = false;
      }
    });
  }

  toggleMap() {
    if (this.activeMap === "comfort") {
      // Switch to public_avg
      this.mapContainer1Target.style.visibility = "hidden";
      this.mapContainer2Target.style.visibility = "visible";
      this.activeMap = "public";
      this.toggleButtonTarget.textContent = "Show Comfort Map";
      // Update window.map reference for backward compatibility
      window.map = this.map2;
    } else {
      // Switch to comfort_avg
      this.mapContainer2Target.style.visibility = "hidden";
      this.mapContainer1Target.style.visibility = "visible";
      this.activeMap = "comfort";
      this.toggleButtonTarget.textContent = "Show Public Map";
      // Update window.map reference for backward compatibility
      window.map = this.map1;
    }
  }

  setDefaultZoom(params, map) {
    const hasUrlParams =
      params.get("lat") && params.get("lon") && params.get("zoom");

    if (!hasUrlParams) {
      if (this.siteSizeValue === 1 && !this.superAdminValue) {
        map.setZoom(14);
      } else if (this.siteSizeValue > 1 && !this.superAdminValue) {
        map.setZoom(8);
      } else {
        map.setZoom(14);
      }
    }
  }

  setupURLTracking(map) {
    const updateURL = () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("share")) {
        const center = map.getCenter();
        const zoom = map.getZoom();

        const url = new URL(window.location);
        url.searchParams.set("lat", center.lat.toFixed(6));
        url.searchParams.set("lon", center.lng.toFixed(6));
        url.searchParams.set("zoom", zoom.toFixed(2));

        window.history.replaceState({}, "", url);
      }
    };

    map.on("dragend", updateURL);
    map.on("zoomend", updateURL);
  }

  setupZoomWarning(map) {
    map.on("zoom", () => {
      const zoomWarnElement = document.querySelector(
        ".public-map-filter-container__zoom-warn",
      );
      const zoomLevel = map.getZoom();
      if (zoomWarnElement) {
        if (zoomLevel < 12) {
          zoomWarnElement.style.display = "block";
        } else {
          zoomWarnElement.style.display = "none";
        }
      }
    });
  }

  initColours() {
    this.unrated_colour = "#7D7B80";
    this.stressed_colour = "#D1504C";
    this.uncomfortable_colour = "#E2B154";
    this.comfortable_colour = "#92c83e";
    this.enjoyable_colour = "#00AA4F";

    this.greyColour = "#7D7B80";
    this.clearColour = "rgba(255,0,255,0)";
  }

  getLayerId(map, mapType) {
    const mapId = map === this.map1 ? "map1" : "map2";
    return `ltr--${mapId}--${mapType}_layer`;
  }

  applyColours(map, mapType) {
    const selectedPalette = document.cookie
      .split("; ")
      .find((row) => row.startsWith("selectedPalette="));
    if (selectedPalette) {
      const paletteName = selectedPalette.split("=")[1];
      this.setPalette(paletteName, map, mapType);
    }
    this.setPreviewColors();

    const mapId = map === this.map1 ? "map1" : "map2";
    const layerId = `ltr--${mapId}--${mapType}_layer`;
    map.setPaintProperty(layerId, "line-color", this.lineColours());
  }

  getCurrentLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          this.map1.setCenter([longitude, latitude]);
          this.map1.setZoom(14);
          this.map2.setCenter([longitude, latitude]);
          this.map2.setZoom(14);

          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get("share")) {
            const url = new URL(window.location);
            url.searchParams.set("lat", latitude.toFixed(6));
            url.searchParams.set("lon", longitude.toFixed(6));
            url.searchParams.set("zoom", "14.00");
            window.history.replaceState({}, "", url);
          }
        },
        (error) => {
          console.error("Error obtaining location:", error);
          alert(
            "Unable to retrieve your location. Please enable location services.",
          );
        },
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  }

  generateTileParams() {
    this.tile_params = this.buildTileParams(
      "?privacy_filter=1&minimum_user_threshold=2",
    );

    return this.tile_params;
  }

  showLoader() {
    const mapContainer = document.getElementById("map-overlay-loader");
    const mapContainerMapbox = document.querySelector(".mapboxgl-map");
    if (mapContainer) {
      mapContainer.classList.add("map-container__insert--loading");
      if (mapContainerMapbox) {
        mapContainerMapbox.style.opacity = "0.3";
      }
      mapContainer.style.zIndex = "1";
    }
    const loader = document.getElementById("comfortmap__loading-container");
    if (loader) {
      loader.style.display = "block";
    }
  }

  hideLoader() {
    const mapContainer = document.getElementById("map-overlay-loader");
    const mapContainerMapbox = document.querySelector(".mapboxgl-map");
    if (mapContainer) {
      mapContainer.classList.remove("map-container__insert--loading");
      if (mapContainerMapbox) {
        mapContainerMapbox.style.opacity = "1";
      }
      mapContainer.style.zIndex = "-1";
    }
    const loader = document.getElementById("comfortmap__loading-container");
    if (loader) {
      loader.style.display = "none";
    }
  }

  setInitialLineThickness(thickness, map, mapType) {
    const mapId = map === this.map1 ? "map1" : "map2";
    const layerId = `ltr--${mapId}--${mapType}_layer`;
    if (map.getLayer(layerId)) {
      map.setPaintProperty(layerId, "line-width", this.lineWidth(thickness));
    }
  }

  resizeMap() {
    if (this.map1) {
      this.map1.resize();
    }
    if (this.map2) {
      this.map2.resize();
    }
  }

  bounds() {
    if (this.hasMaxBoundsValue && this.maxBoundsValue.length > 0) {
      return mapboxgl.LngLatBounds.convert(this.maxBoundsValue);
    } else {
      return undefined;
    }
  }

  maxBounds() {
    if (this.hasMaxBoundsValue && this.maxBoundsValue.length > 0) {
      return mapboxgl.LngLatBounds.convert(this.maxBoundsValue);
    } else {
      return undefined;
    }
  }

  center() {
    if (
      this.hasCentrePointValue &&
      this.centrePointValue.length > 0 &&
      this.centrePointValue[0] != 0 &&
      this.centrePointValue[1] != 0
    ) {
      return new mapboxgl.LngLat(
        this.centrePointValue[0],
        this.centrePointValue[1],
      );
    } else {
      return undefined;
    }
  }

  resetFilterParams() {
    const originalParams = new URLSearchParams(this.tile_params);

    originalParams.delete("timeframe");
    originalParams.delete("purpose");
    originalParams.delete("frequency");
    originalParams.delete("work_travel");
    originalParams.delete("gender");
    originalParams.delete("ethnicity");
    originalParams.delete("age");

    return originalParams;
  }

  buildTileParams(tile_params) {
    const originalParams = this.resetFilterParams();
    const newTileParams = new URLSearchParams(tile_params);
    const urlSearchParams = new URLSearchParams(window.location.search);

    if (urlSearchParams.get("personal")) {
      originalParams.set("personal", urlSearchParams.get("personal"));
    } else if (
      urlSearchParams.get("public") ||
      (this.hasPublicValue && this.publicValue == true)
    ) {
      originalParams.set("public", urlSearchParams.get("public") || true);
    }

    if (urlSearchParams.get("log_level")) {
      originalParams.set("log_level", urlSearchParams.get("log_level"));
    }

    if (urlSearchParams.get("max_age")) {
      originalParams.set("max_age", urlSearchParams.get("max_age"));
    }

    if (urlSearchParams.get("cache_bust")) {
      originalParams.set("cache_bust", urlSearchParams.get("cache_bust"));
    }

    if (this.hasHideUnratedValue && this.hideUnratedValue == true) {
      originalParams.set("hide_unrated", this.hideUnratedValue);
    } else {
      originalParams.delete("hide_unrated");
    }

    originalParams.set("precision", originalParams.get("precision") || 1);

    if (!urlSearchParams.get("public")) {
      originalParams.set("token", this.userTokenValue);
      originalParams.set("site_id", this.siteIdValue);
    }

    newTileParams.forEach(function (value, key) {
      originalParams.set(key, value);
    });

    return "?" + originalParams.toString();
  }

  updateTileParams(tile_params, onLoad = false, map, mapType) {
    if (!map || typeof map.getSource !== "function") {
      console.error("Map is not initialized.");
      return;
    }

    this.tile_params = this.buildTileParams(tile_params);

    let firstSymbolId = this.getFirstSymbolId(map);
    const mapId = map === this.map1 ? "map1" : "map2";
    const layerId = `ltr--${mapId}--${mapType}_layer`;

    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }

    this.insertLayer(
      mapType,
      this.tile_params,
      firstSymbolId,
      this.line_width,
      map,
    );

    if (
      !onLoad &&
      this.element.dataset.controller.includes("maps--pin-map-layer")
    ) {
      this.togglePins(true, map, mapType);
    }
  }

  flyAndZoom(targetZoom) {
    if (this.map1) {
      this.map1.setZoom(targetZoom);
    }
    if (this.map2) {
      this.map2.setZoom(targetZoom);
    }
  }

  setLocation({ detail }) {
    const {
      lat,
      lon,
      tile_params = {},
      line_thickness,
      hide_unrated,
      hide_status_bar,
      line_scale,
      palette,
      base_map,
      show_hide_routes,
      show_hide_rated_routes,
      selected_route_colors,
      hide_routes_show_gray,
      show_hide_pins,
    } = detail;

    if (lat && lon) {
      this.setMapLocation(lat, lon);
      return;
    }

    if (line_thickness) {
      this.setLineThickness(line_thickness, this.map1, "comfort_avg");
      this.setLineThickness(line_thickness, this.map2, "public_avg");
      return;
    }

    if (line_scale) {
      this.setLineScale(line_scale);
      return;
    }

    if (palette) {
      this.setPalette(palette, this.map1, "comfort_avg");
      this.setPalette(palette, this.map2, "public_avg");
      return;
    }

    if (hide_unrated !== undefined) {
      this.toggleUnratedVisibility(hide_unrated);
      return;
    }

    if (hide_routes_show_gray !== undefined) {
      this.hideRoutesShowGray();
      return;
    }

    if (hide_status_bar !== undefined) {
      this.toggleStatusBar(hide_status_bar);
      return;
    }

    if (base_map) {
      this.setBaseMapStyle(base_map);
      return;
    }

    if (show_hide_routes !== undefined) {
      this.toggleRouteVisibility(show_hide_routes);
      return;
    }

    if (selected_route_colors) {
      this.applySelectedRouteColors(selected_route_colors);
      return;
    }

    if (show_hide_rated_routes !== undefined) {
      this.toggleRatedRoutes(show_hide_rated_routes);
      return;
    }

    if (show_hide_pins !== undefined) {
      this.togglePins(show_hide_pins, this.map1, "comfort_avg");
      this.togglePins(show_hide_pins, this.map2, "public_avg");
      return;
    }

    this.updateTileParams(tile_params, false, this.map1, "comfort_avg");
    this.updateTileParams(tile_params, false, this.map2, "public_avg");
  }

  setMapLocation(lat, lon) {
    // Disable sync during programmatic location updates to prevent conflicts
    this.syncingPan = true;

    if (this.siteSizeValue > 1 && !this.superAdminValue) {
      this.map1.setCenter([lon, lat]);
      this.map1.setZoom(14);
      this.map2.setCenter([lon, lat]);
      this.map2.setZoom(14);

      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("share")) {
        const url = new URL(window.location);
        url.searchParams.set("lat", lat.toFixed(6));
        url.searchParams.set("lon", lon.toFixed(6));
        url.searchParams.set("zoom", "14.00");
        window.history.replaceState({}, "", url);
      }
    } else {
      this.map1.flyTo({ center: [lon, lat] });
      this.map2.flyTo({ center: [lon, lat] });

      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("share")) {
        const url = new URL(window.location);
        url.searchParams.set("lat", lat.toFixed(6));
        url.searchParams.set("lon", lon.toFixed(6));
        window.history.replaceState({}, "", url);
      }
    }

    // Re-enable sync after a short delay to allow the animation to complete
    setTimeout(() => {
      this.syncingPan = false;
    }, 100);
  }

  setLineThickness(line_thickness, map, mapType) {
    this.line_width = parseInt(line_thickness);
    map.setPaintProperty(
      this.getLayerId(map, mapType),
      "line-width",
      this.lineWidth(this.line_width),
    );
  }

  setLineScale(line_scale) {
    this.line_scale = line_scale;
    this.map1.setPaintProperty(
      this.getLayerId(this.map1, "comfort_avg"),
      "line-width",
      this.lineWidth(this.line_width),
    );
    this.map2.setPaintProperty(
      this.getLayerId(this.map2, "public_avg"),
      "line-width",
      this.lineWidth(this.line_width),
    );
  }

  setPalette(paletteName, map, mapType) {
    document.cookie = `selectedPalette=${paletteName}; path=/; max-age=${
      60 * 60 * 24 * 30
    }`;

    const palette = this.fetchPalette(paletteName);

    this.unrated_colour = palette.unrated_colour;
    this.stressed_colour = palette.stressed_colour;
    this.uncomfortable_colour = palette.uncomfortable_colour;
    this.comfortable_colour = palette.comfortable_colour;
    this.enjoyable_colour = palette.enjoyable_colour;

    if (this.lastSelectedRouteColors.length > 0) {
      this.applySelectedRouteColors(this.lastSelectedRouteColors);
    } else {
      map.setPaintProperty(
        this.getLayerId(map, mapType),
        "line-color",
        this.lineColours(),
      );
    }

    this.updateComfortColor(
      [".unrated", ".rating-0", ".map-settings__feeling--unrated"],
      this.unrated_colour,
    );
    this.updateComfortColor(
      [".stressed", ".rating-1", ".map-settings__feeling--stressed"],
      this.stressed_colour,
    );
    this.updateComfortColor(
      [".uncomfortable", ".rating-2", ".map-settings__feeling--uncomfortable"],
      this.uncomfortable_colour,
    );
    this.updateComfortColor(
      [".comfortable", ".rating-3", ".map-settings__feeling--comfortable"],
      this.comfortable_colour,
    );
    this.updateComfortColor(
      [".enjoyable", ".rating-4", ".map-settings__feeling--enjoyable"],
      this.enjoyable_colour,
    );

    const paletteElement = document.getElementsByClassName(
      `comfort-map__select-div-palette-preview palette-${paletteName}`,
    )[0];
    if (paletteElement) {
      paletteElement.classList.add("active");
    }
  }

  fetchPalette(paletteName) {
    switch (paletteName) {
      case "paul_tol_muted":
        return {
          unrated_colour: "#DDDDDD",
          stressed_colour: "#EE6677",
          uncomfortable_colour: "#AA3377",
          comfortable_colour: "#66CCEE",
          enjoyable_colour: "#44AA66",
        };
      case "okabe_ito":
        return {
          unrated_colour: "#000000",
          stressed_colour: "#E69F00",
          uncomfortable_colour: "#CC79A7",
          comfortable_colour: "#0072B2",
          enjoyable_colour: "#009E73",
        };
      case "paul_tol_bright":
        return {
          unrated_colour: "#DDDDDD",
          stressed_colour: "#EE6677",
          uncomfortable_colour: "#CCBB44",
          comfortable_colour: "#4477AA",
          enjoyable_colour: "#228833",
        };
      case "ibm_design":
        return {
          unrated_colour: "#785EF0",
          stressed_colour: "#FE6100",
          uncomfortable_colour: "#FFB000",
          comfortable_colour: "#648FFF",
          enjoyable_colour: "#DC267F",
        };
      default:
        return {
          unrated_colour: "#7D7B80",
          stressed_colour: "#D1504C",
          uncomfortable_colour: "#E2B154",
          comfortable_colour: "#92c83e",
          enjoyable_colour: "#00AA4F",
        };
    }
  }

  setPreviewColors() {
    [
      "normal",
      "paul_tol_muted",
      "okabe_ito",
      "paul_tol_bright",
      "ibm_design",
    ].forEach((paletteName) => {
      if (
        !document.querySelector(`.palette-preview--${paletteName}-stressed`)
      ) {
        return;
      }

      const palette = this.fetchPalette(paletteName);
      document.querySelector(
        `.palette-preview--${paletteName}-stressed`,
      ).style.backgroundColor = palette.stressed_colour;
      document.querySelector(
        `.palette-preview--${paletteName}-uncomfortable`,
      ).style.backgroundColor = palette.uncomfortable_colour;
      document.querySelector(
        `.palette-preview--${paletteName}-comfortable`,
      ).style.backgroundColor = palette.comfortable_colour;
      document.querySelector(
        `.palette-preview--${paletteName}-enjoyable`,
      ).style.backgroundColor = palette.enjoyable_colour;
      document.querySelector(
        `.palette-preview--${paletteName}-unrated`,
      ).style.backgroundColor = palette.unrated_colour;
    });
  }

  updateComfortColor(classNames, color) {
    for (const sheet of document.styleSheets) {
      if (sheet.href && new URL(sheet.href).origin !== window.location.origin) {
        continue;
      }

      const rules = sheet.cssRules || sheet.rules;

      for (let i = 0; i < rules.length; i++) {
        if (classNames.includes(rules[i].selectorText)) {
          rules[i].style.backgroundColor = color;
          rules[i].style.color = color;
        }
      }
    }
  }

  toggleUnratedVisibility(hide_unrated) {
    this.unrated_colour = hide_unrated ? this.greyColour : this.clearColour;
    this.map1.setPaintProperty(
      this.getLayerId(this.map1, "comfort_avg"),
      "line-color",
      this.lineColours(),
    );
    this.map2.setPaintProperty(
      this.getLayerId(this.map2, "public_avg"),
      "line-color",
      this.lineColours(),
    );
  }

  hideRoutesShowGray() {
    this.map1.setPaintProperty(
      this.getLayerId(this.map1, "comfort_avg"),
      "line-color",
      this.lineColoursG(),
    );
    this.map2.setPaintProperty(
      this.getLayerId(this.map2, "public_avg"),
      "line-color",
      this.lineColoursG(),
    );
  }

  toggleStatusBar(hide_status_bar) {
    if (hide_status_bar) {
      this.statusBarTarget.classList.add("hidden");
    } else {
      this.statusBarTarget.classList.remove("hidden");
    }
  }

  setBaseMapStyle(base_map) {
    this.changeMapStyle(base_map, this.map1);
    this.changeMapStyle(base_map, this.map2);
  }

  mapStyles() {
    return {
      light: {
        sprite: "mapbox://sprites/mapbox/light-v11",
        name: "MapBox Light",
        sources: {
          "mapbox-streets": {
            type: "vector",
            url: "mapbox://mapbox.mapbox-streets-v8",
          },
        },
        layers: [
          {
            id: "streets-base",
            type: "background",
            paint: { "background-color": "#ffffff" },
          },
        ],
      },
      satellite: {
        sprite: "mapbox://sprites/mapbox/satellite-v9",
        name: "MapBox Satelite",
        sources: {
          "mapbox-satellite": {
            type: "raster",
            url: "mapbox://mapbox.satellite",
            tileSize: 256,
          },
        },
        layers: [
          {
            id: "satellite",
            type: "raster",
            source: "mapbox-satellite",
            minzoom: 0,
            maxzoom: 22,
          },
        ],
      },
    };
  }

  changeMapStyle(styleKey, map) {
    const style = this.mapStyles()[styleKey] ?? this.mapStyles("light");
    const prevStyle = map.getStyle();

    map.setStyle({
      ...prevStyle,
      sprite: prevStyle.sprite,
      sources: {
        ...style.sources,
        ...Object.entries(prevStyle.sources)
          .filter(([id]) => id.startsWith("ltr--"))
          .reduce((acc, [id, source]) => ({ ...acc, [id]: source }), {}),
      },
      layers: [
        ...style.layers,
        ...prevStyle.layers.filter((layer) => layer.id.startsWith("ltr--")),
      ],
    });
  }

  toggleRouteVisibility(show_hide_routes) {
    this.lastSelectedRouteColors = [];
    const color = show_hide_routes
      ? this.lineColours()
      : this.lineColoursGrey();
    this.map1.setPaintProperty(
      this.getLayerId(this.map1, "comfort_avg"),
      "line-color",
      color,
    );
    this.map2.setPaintProperty(
      this.getLayerId(this.map2, "public_avg"),
      "line-color",
      color,
    );
  }

  applySelectedRouteColors(selected_route_colors) {
    this.lastSelectedRouteColors = selected_route_colors;
    this.map1.setPaintProperty(
      this.getLayerId(this.map1, "comfort_avg"),
      "line-color",
      this.lineColoursDynamicMulti(selected_route_colors),
    );
    this.map2.setPaintProperty(
      this.getLayerId(this.map2, "public_avg"),
      "line-color",
      this.lineColoursDynamicMulti(selected_route_colors),
    );
  }

  toggleRatedRoutes(show_hide_rated_routes) {
    if (show_hide_rated_routes) {
      this.map1.setPaintProperty(
        this.getLayerId(this.map1, "comfort_avg"),
        "line-color",
        this.lineColours(),
      );
      this.map2.setPaintProperty(
        this.getLayerId(this.map2, "public_avg"),
        "line-color",
        this.lineColours(),
      );
    }
  }

  togglePins(show_hide_pins, map, mapType) {
    if (show_hide_pins) {
      this.insertLayer(
        "pins",
        this.tile_params,
        this.getFirstSymbolId(map),
        4,
        map,
      );
    } else {
      map.removeLayer("ltr--pin_single_layer");
      map.removeLayer("ltr--pin_cluster_layer");
      map.removeLayer("ltr--pin_count");
    }
  }

  setBaseUrl() {
    if (
      document.getElementsByTagName("main")[0].classList.contains("development")
    ) {
      this.baseUrl = "http://localhost:3002";
    } else {
      this.baseUrl = "https://journey.lovetoride.net";
    }
  }

  initComfortMap(params, map, mapType) {
    let lat = "";
    let lon = "";
    let zoom = "";

    if (this.siteSizeValue > 0 && !this.superAdminValue) {
      lat = params.get("lat");
      lon = params.get("lon");
      zoom = params.get("zoom");
    } else {
      lat = params.get("lat") || 51.444229;
      lon = params.get("lon") || -2.563627;
      zoom = params.get("zoom");
    }

    if (lat != null && lon != null) {
      map.setCenter([parseFloat(lon), parseFloat(lat)]);
    }

    if (zoom != null) {
      map.setZoom(parseFloat(zoom));
    } else if (lat != null && lon != null) {
      map.setZoom(this.defaultZoom);
    }

    let firstSymbolId = this.getFirstSymbolId(map);

    var max_age = params.get("max_age");
    if (max_age == null) {
      max_age = 3600;
    }
    var tile_params =
      "?max_age=" +
      max_age +
      "&cache_bust=" +
      params.get("cache_bust") +
      "&precision=1";

    const urlSearchParams = new URLSearchParams(window.location.search);
    if (urlSearchParams.get("personal")) {
      tile_params = tile_params + "&personal=true";
    } else if (urlSearchParams.get("public")) {
      tile_params = tile_params + "&public=true";
    }

    if (!urlSearchParams.get("public")) {
      tile_params =
        tile_params +
        "&token=" +
        this.userTokenValue +
        "&site_id=" +
        this.siteIdValue;
    }

    let debug_options = params.get("debug_trip") || "";
    if (debug_options.includes("hu")) {
      this.unrated_colour = this.clearColour;
    }

    const lineWidth = params.get("line_width") || "5";
    this.insertLayer(
      mapType,
      this.tile_params,
      firstSymbolId,
      parseInt(lineWidth),
      map,
    );

    const layerId = this.getLayerId(map, mapType);
    map.on("mouseenter", layerId, (e) => {
      this.updateStatusBar(e.lngLat, e.features[0].properties);
    });

    const hasUrlParams =
      params.get("lat") && params.get("lon") && params.get("zoom");

    if (!hasUrlParams) {
      if (this.maxBounds() != undefined) {
        map.fitBounds(this.maxBounds());
        map.setMinZoom(0);
      } else if (this.center() != undefined) {
        map.jumpTo({ center: this.center() });
        map.setZoom(this.defaultZoom);
      }
    }
  }

  addPublicMapWatermark() {
    // Prevent duplication if the map reloads
    if (document.getElementById("public-map-watermark")) {
      return;
    }

    // Create watermark element to hint at public data
    const watermark = document.createElement('div');
    watermark.id = 'public-map-watermark';
    watermark.innerHTML = 'Experimental<br>Map Data';
    watermark.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 72px;
      font-weight: bold;
      color: rgba(255, 0, 0, 0.15);
      text-align: center;
      pointer-events: none;
      z-index: 1000;
      font-family: Arial, sans-serif;
      line-height: 1.2;
      user-select: none;
    `;

    // Add to map2 container
    this.mapContainer2Target.appendChild(watermark);
  }

  drawBounds(map) {
    let bounds = this.maxBounds();
    if (bounds == undefined) {
      return;
    }

    map.addLayer({
      id: "ltr--line-bounding-box",
      type: "line",
      paint: {
        "line-color": "#666",
        "line-width": 3,
        "line-opacity": 0.9,
      },
      source: {
        type: "geojson",
        data: {
          type: "Feature",
          properties: {},
          geometry: {
            type: "Polygon",
            coordinates: [
              [
                bounds.getSouthWest().toArray(),
                bounds.getSouthEast().toArray(),
                bounds.getNorthEast().toArray(),
                bounds.getNorthWest().toArray(),
                bounds.getSouthWest().toArray(),
              ],
            ],
          },
        },
      },
    });
  }

  bracket_highest_value_to_text(value) {
    if (this.hasSuperAdminValue && this.superAdminValue) {
      return value;
    } else {
      let mapping = {
        0: "0",
        10: "1–10",
        20: "11–20",
        50: "21–50",
        100: "51–100",
        200: "101–200",
        500: "201–500",
        1000: "501–1000",
        2000: "1001-2000",
      };

      if (mapping[value] == undefined) {
        return value - 999 + "-" + value;
      }
      return mapping[value];
    }
  }

  updateStatusBar(lngLat, properties) {
    if (this.hasStatusBarTarget) {
      console.log(properties);
      var html = "";
      if (this.hasSuperAdminValue && this.superAdminValue) {
        html += `${lngLat} - <a target="_blank" href="https://metabase.lovetoride.net/question/1123-segment-debug?segment_id=${properties.segment_id}">Segment ${properties.segment_id}</a>
         From <a target="_blank" href="https://www.openstreetmap.org/way/${properties.osm_id}">OSM ${properties.osm_id}</a>`;
      }
      html += `
         <span>Trips: ${this.bracket_highest_value_to_text(
           properties.trips_count,
         )}</span>
         <span>Ratings: ${this.bracket_highest_value_to_text(
           properties.ratings_count,
         )}</span>
         <span>Unique raters: ${this.bracket_highest_value_to_text(
           properties.unique_raters_count,
         )}</span>
         <span>Unique riders: ${this.bracket_highest_value_to_text(
           properties.unique_riders_count,
         )}</span>
  `;
      if (properties.rating_value != null) {
        html += `&nbsp;<span class="rating-${properties.rating_value}">Average rating value ${properties.rating_value}</span>`;
      }
      this.statusBarTarget.innerHTML = html;
    }
  }

  getFirstSymbolId(map) {
    for (const layer of map.getStyle().layers) {
      if (layer.type === "symbol") {
        return layer.id;
      }
    }
    return null;
  }

  lineColours() {
    return [
      "interpolate",
      ["linear"],
      ["to-number", ["get", "rating_value"]],
      0,
      this.unrated_colour,
      1,
      this.stressed_colour,
      2,
      this.uncomfortable_colour,
      3,
      this.comfortable_colour,
      4,
      this.enjoyable_colour,
    ];
  }

  lineColoursG() {
    return [
      "interpolate",
      ["linear"],
      ["to-number", ["get", "rating_value"]],
      0,
      "#7D7B80",
      1,
      "#7D7B80",
      2,
      "#7D7B80",
      3,
      "#7D7B80",
      4,
      "#7D7B80",
    ];
  }

  lineColoursGrey(rating) {
    const ratingToColor = {
      unrated: this.unrated_colour,
    };

    const colors = new Array(5).fill(ratingToColor.unrated);

    Object.entries(ratingToColor).forEach(([key, color], index) => {
      if (rating === key) {
        colors[index] = color;
      }
    });

    return [
      "interpolate",
      ["linear"],
      ["to-number", ["get", "rating_value"]],
      ...colors.flatMap((color, index) => [index, color]),
    ];
  }

  lineColoursDynamicMulti(selectedColors) {
    const ratingToColor = {
      unrated: this.unrated_colour,
      stressed: this.stressed_colour,
      uncomfortable: this.uncomfortable_colour,
      comfortable: this.comfortable_colour,
      enjoyable: this.enjoyable_colour,
    };

    const colors = new Array(5).fill(this.clearColour);

    selectedColors.forEach((color) => {
      const index = Object.keys(ratingToColor).indexOf(color);
      if (index !== -1) {
        colors[index] = ratingToColor[color];
      }
    });

    return [
      "interpolate",
      ["linear"],
      ["to-number", ["get", "rating_value"]],
      ...colors.flatMap((color, index) => [index, color]),
    ];
  }

  interpolatedLineWidthBy(attributeName, baseLineWidth) {
    return [
      "interpolate",
      ["linear"],
      ["to-number", ["get", attributeName]],
      0,
      0.25 * baseLineWidth,
      100,
      5 * baseLineWidth,
      1000,
      10 * baseLineWidth,
    ];
  }

  lineWidth(baseLineWidth) {
    let result = null;
    switch (this.line_scale) {
      case "unique_riders_count":
        result = this.interpolatedLineWidthBy(this.line_scale, baseLineWidth);
        break;
      case "unique_raters_count":
        result = this.interpolatedLineWidthBy(this.line_scale, baseLineWidth);
        break;
      case "trips_count":
        result = this.interpolatedLineWidthBy(this.line_scale, baseLineWidth);
        break;
      case "ratings_count":
        result = this.interpolatedLineWidthBy(this.line_scale, baseLineWidth);
        break;
      default:
        result = this.line_width;
        break;
    }
    return result;
  }

  loadPinImages(map) {
    if (!map.hasImage("pin-icon")) {
      map.loadImage(this.pinImageUrlValue, (error, image) => {
        if (error) throw error;
        map.addImage("pin-icon", image);
      });
    }
    if (!map.hasImage("pin-cluster-icon")) {
      map.loadImage(this.pinClusterImageUrlValue, (error, image) => {
        if (error) throw error;
        map.addImage("pin-cluster-icon", image);
      });
    }
  }

  insertLayer(layer, tile_params, firstSymbolId, baseLineWidth, map) {
    if (layer === "comfort_avg" || layer === "public_avg") {
      // Use unique source and layer IDs per map instance to prevent caching conflicts
      const mapId = map === this.map1 ? "map1" : "map2";
      const sourceId = `ltr--${mapId}--${layer}`;
      const layerId = `ltr--${mapId}--${layer}_layer`;

      if (map.getSource(sourceId)) {
        map
          .getSource(sourceId)
          .setTiles([
            `${this.baseUrl}/api/tiles/{z}/{x}/{y}.mvt${tile_params}&comfort=${layer}`,
          ])
          .reload();
      } else {
        map.addSource(sourceId, {
          type: "vector",
          tiles: [
            `${this.baseUrl}/api/tiles/{z}/{x}/{y}.mvt${tile_params}&comfort=${layer}`,
          ],
          minzoom: 12,
          maxzoom: 15,
        });
      }

      if (!map.getLayer(layerId)) {
        map.addLayer(
          {
            id: layerId,
            type: "line",
            source: sourceId,
            "source-layer": "default",
            layout: {
              "line-cap": "round",
              "line-join": "miter",
            },
            paint: {
              "line-color": this.lineColours(),
              "line-width": this.lineWidth(baseLineWidth),
            },
          },
          firstSymbolId,
        );
      } else {
        map.setPaintProperty(layerId, "line-color", "#92c83e");
        map.setPaintProperty(
          layerId,
          "line-width",
          this.lineWidth(baseLineWidth),
        );
      }
    }

    if (layer == "pins") {
      if (map.getSource("ltr--pins")) {
        map
          .getSource("ltr--pins")
          .setTiles([
            `${this.baseUrl}/api/pin_tiles/{z}/{x}/{y}.mvt${tile_params}`,
          ])
          .reload();
      } else {
        map.addSource("ltr--pins", {
          type: "vector",
          tiles: [
            `${this.baseUrl}/api/pin_tiles/{z}/{x}/{y}.mvt${tile_params}`,
          ],
          minzoom: 3,
          maxzoom: 15,
        });
      }
      this.loadPinImages(map);

      if (!map.getLayer("ltr--pin_cluster_layer")) {
        map.addLayer({
          id: "ltr--pin_single_layer",
          type: "symbol",
          source: "ltr--pins",
          "source-layer": "default",
          filter: ["==", ["get", "pin_count"], 1],
          layout: {
            "icon-image": "pin-icon",
            "icon-size": 0.25,
            "icon-allow-overlap": true,
            "icon-anchor": "bottom",
            "icon-opacity": 1.0,
          },
        });

        map.addLayer({
          id: "ltr--pin_cluster_layer",
          type: "symbol",
          source: "ltr--pins",
          "source-layer": "default",
          filter: [">", ["get", "pin_count"], 1],
          layout: {
            "icon-image": "pin-cluster-icon",
            "icon-size": 0.25,
            "icon-allow-overlap": true,
            "icon-anchor": "center",
            "icon-opacity": 1.0,
          },
        });

        map.addLayer({
          id: "ltr--pin_count",
          type: "symbol",
          source: "ltr--pins",
          "source-layer": "default",
          filter: [">", ["get", "pin_count"], 1],
          layout: {
            "text-field": ["get", "pin_count"],
            "text-size": 20,
            "text-weight": "bold",
          },
          paint: {
            "text-color": "#ffffff",
          },
        });
      }
    }
  }
}
